import { Button } from "@/components/ui/baseUi/button"
import { useAppForm } from "@/components/ui/forms/hooks/useFormHook"
import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { useUser } from "@/modules/users/hooks/useUser"
import { word } from "@modules/core/utils/i18n"
import { DEFAULT_MAXIMUM_AMOUNT, formatCurrency } from "@modules/currency/currency"
import { FinanceFeesAlertCard } from "@modules/finances/components/financeFeesAlertCard"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { useProgram, useProgramUpdatePrice } from "@modules/programs/hooks/usePrograms"
import { getProgramPriceSchema } from "@modules/programs/types/programFormikType"
import { ProgramType } from "@modules/programs/types/programTypes"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { useImperativeHandle } from "react"

type ProgramPriceFormProps = {
  programId: string
  className?: string
  ref: React.Ref<ProgramPriceFormRef>
}

export type ProgramPriceFormRef = {
  onClose: () => Promise<boolean>
}

export function ProgramPriceForm({ programId, className, ref }: ProgramPriceFormProps) {
  const language = useLanguage()
  const user = useUser()
  const { sidebarNavigateBack } = useSidebar()
  const confirmPopup = useConfirmPopup()
  const { data: program } = useProgram(programId)
  const { updateProgramPrice, isUpdatingProgramPrice } = useProgramUpdatePrice()

  const form = useAppForm({
    defaultValues: program.getProgramPriceForm(user.stripeAccountCurrency ?? undefined),
    validators: {
      onSubmit: getProgramPriceSchema(
        user.stripeMinimumAmount ?? 0,
        user.stripeMaximumAmount ?? DEFAULT_MAXIMUM_AMOUNT,
      ),
      onBlur: getProgramPriceSchema(user.stripeMinimumAmount ?? 0, user.stripeMaximumAmount ?? DEFAULT_MAXIMUM_AMOUNT),
    },
    onSubmit: async ({ value }) => {
      await updateProgramPrice({
        programId: program.id,
        price: {
          value: value.value ?? 0,
          currency: value.currency,
        },
        trialDuration: program.type === ProgramType.VIRTUAL ? value.trialDuration : null,
      })
        .then(() => {
          toast.success(word("program.switchPrice.success"), {
            classNames: successToastClassName(),
          })
          sidebarNavigateBack(false)
        })
        .catch((e: unknown) => {
          toast.error(e instanceof Error ? e.message : String(e), {
            classNames: errorToastClassName(),
          })
        })
    },
  })

  useImperativeHandle(ref, () => ({
    onClose: async () => {
      if (!form.state.isPristine) {
        const confirm = await new Promise<boolean>((resolve) => {
          confirmPopup.show({
            title: word("program.price.form.closeMessage.title"),
            message: word("program.price.form.closeMessage.message"),
            accept: () => {
              resolve(true)
              return Promise.resolve()
            },
            cancel: () => {
              resolve(false)
              return Promise.resolve()
            },
            footerProps: {
              confirmText: word("global.ok"),
            },
          })
        })

        return confirm
      }
      return Promise.resolve(true)
    },
  }))

  return (
    <div className={cn("flex w-full flex-col gap-4 px-5 pt-18 pb-10", className)}>
      <Text fontStyle="TL">
        {program.type === ProgramType.VIRTUAL ? word("program.price.membershipPrice") : word("program.onOff.price")}
      </Text>
      <Text fontStyle="BS">
        {program.type === ProgramType.VIRTUAL
          ? word("program.price.membershipPriceDescription")
          : word("program.onOff.priceDescription")}
      </Text>

      <form.AppField name="value">
        {(field) => (
          <field.Field>
            <field.Label required>{word("program.price.label")}</field.Label>
            <field.Content>
              <div className="inline-flex flex-row items-center gap-2">
                <field.PriceInput
                  currency={user.stripeAccountCurrency ?? undefined}
                  locale={language}
                  inputProps={{
                    placeholder: word("offer.form.price.placeholder", {
                      minPrice: formatCurrency(user.stripeMinimumAmount ?? 0, user.stripeAccountCurrency, language),
                    }),
                  }}
                />
                <Text fontStyle="TM" className="flex w-fit items-center justify-center pr-2 whitespace-nowrap">
                  {`/ ${word("global.month.label", { count: 1 })}`}
                </Text>
              </div>
              <field.Message />
            </field.Content>
          </field.Field>
        )}
      </form.AppField>

      {program.type === ProgramType.VIRTUAL && (
        <div className="flex w-full flex-col gap-4">
          <Text className="mb-0 text-textDefault-caption" fontStyle="LS" style={{ lineHeight: "21px" }}>
            {word("program.price.trial.label").toUpperCase()}
          </Text>
          <div className="card mt-0 flex w-full flex-col items-center gap-4">
            <form.AppField
              name="addFreeTrial"
              listeners={{
                onChange: ({ value, fieldApi }) => {
                  if (!value) fieldApi.form.setFieldValue("trialDuration", null)
                },
              }}
            >
              {(field) => (
                <field.Field>
                  <field.Label required>{word("program.price.trial.label")}</field.Label>
                  <field.Content>
                    <div className="flex w-full flex-row items-center justify-between rounded-2xl bg-surface-foreground px-5 py-3.5 transition-all">
                      <Text fontStyle="LL" className="text-foreground">
                        {word("program.price.trial.inputLabel")}
                      </Text>
                      <field.Switch />
                    </div>
                    <field.Message />
                  </field.Content>
                </field.Field>
              )}
            </form.AppField>

            <form.Subscribe selector={(state) => state.values.addFreeTrial}>
              {(addFreeTrial) => (
                <form.AppField name="trialDuration">
                  {(field) => (
                    <field.Field>
                      <field.Content>
                        <div className="inline-flex flex-row gap-2">
                          <field.NumberInput
                            step={1}
                            max={365}
                            min={1}
                            disabled={!addFreeTrial}
                            className="bg-surface-background"
                            placeholder={word("program.onOff.formik.placeholder")}
                          />
                          <Text fontStyle="TM" className="flex w-fit items-center justify-center pr-2 pl-6">
                            {word("global.day.label", { count: 2 })}
                          </Text>
                        </div>
                        <field.Message />
                      </field.Content>
                    </field.Field>
                  )}
                </form.AppField>
              )}
            </form.Subscribe>
          </div>
        </div>
      )}

      <FinanceFeesAlertCard />

      <form.Subscribe selector={(state) => [state.isSubmitting, state.canSubmit]}>
        {([isSubmitting, canSubmit]) => (
          <Button
            variant="primary"
            className="w-full"
            disabled={!canSubmit}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              void form.handleSubmit()
            }}
            isLoading={isSubmitting || isUpdatingProgramPrice}
          >
            {program.price ? word("global.update") : word("global.create")}
          </Button>
        )}
      </form.Subscribe>
    </div>
  )
}
